package dev.lingvo.shared

import kotlinx.serialization.Serializable

@Serializable
data class LocaleProgressDto(
    val locale: String,
    val total: Int = 0,
    val translated: Int = 0,
    val approved: Int = 0,
    val percentTranslated: Double = 0.0,
    val percentApproved: Double = 0.0,
)

@Serializable
data class ProjectSummaryDto(
    val id: String,
    val orgId: String,
    /**
     * The slug of the workspace that owns this project.
     *
     * Present alongside [orgId] because every link that needs it is a URL —
     * `/app/settings/members`, the workspace switcher, the billing page — and a UUID cannot be one.
     * Without it the web app had to look the slug up from the session by matching on [orgId], which
     * silently produced no link at all whenever the session had not loaded yet.
     */
    val orgSlug: String = "",
    val name: String,
    val slug: String,
    val sourceLocale: String,
    val createdAt: String,
    val updatedAt: String,
    val kind: ProjectKind = ProjectKind.OTHER,
    val iconKey: ProjectIconKey = ProjectIconKey.MONOGRAM,
    /**
     * Whether this project has an uploaded mark, which supersedes [iconKey] wherever it is drawn.
     *
     * A flag, never the bytes. An icon rides in every project list and every breadcrumb; inlining
     * even 20KB of base64 into each would cost more than the page it decorates. The image is fetched
     * once from its own endpoint and cached by the browser like any other.
     */
    val hasCustomIcon: Boolean = false,
    val accentKey: ProjectAccentKey = ProjectAccentKey.INK,
    val description: String? = null,
    /**
     * Standing instructions for translating this product.
     *
     * The thing a team tells every new translator and then has to tell again — "address the user as
     * du, never Sie", "keep button labels under two words". Distinct from [description], which says
     * what the product is, and from a run's tone, which is a knob somebody turns once. It reaches
     * both the machine-translation prompt and every agent, which is what makes it worth writing.
     */
    val styleGuide: String? = null,
    val locales: List<String> = emptyList(),
    val progress: List<LocaleProgressDto> = emptyList(),
    val keyCount: Int = 0,
    val fileCount: Int = 0,
    val localeCount: Int = 0,
)

@Serializable
data class EditorCapabilitiesDto(
    val canWriteTranslations: Boolean = false,
    val canApproveTranslations: Boolean = false,
    /**
     * Whether this person may spend the workspace's machine-translation credits.
     *
     * Role ≥ ADMIN **and** the credential carries `mt:spend`, which is the same pair the spend
     * routes check. Separate from [canWriteTranslations] because they diverge in both directions: a
     * translator may type all day and start no runs, and a `strings:write` CI token may write
     * thousands of strings without ever being allowed to buy one.
     *
     * Answered by the server rather than inferred from the role client-side, because the token half
     * of it is invisible to the browser — inferring it renders a pre-translate button that 403s.
     */
    val canSpendCredits: Boolean = false,
)

@Serializable
data class ProjectDetailDto(
    val id: String,
    val orgId: String,
    /**
     * The slug of the workspace that owns this project.
     *
     * Present alongside [orgId] because every link that needs it is a URL —
     * `/app/settings/members`, the workspace switcher, the billing page — and a UUID cannot be one.
     * Without it the web app had to look the slug up from the session by matching on [orgId], which
     * silently produced no link at all whenever the session had not loaded yet.
     */
    val orgSlug: String = "",
    val name: String,
    val slug: String,
    val sourceLocale: String,
    val createdAt: String,
    val updatedAt: String,
    val kind: ProjectKind = ProjectKind.OTHER,
    val iconKey: ProjectIconKey = ProjectIconKey.MONOGRAM,
    /**
     * Whether this project has an uploaded mark, which supersedes [iconKey] wherever it is drawn.
     *
     * A flag, never the bytes. An icon rides in every project list and every breadcrumb; inlining
     * even 20KB of base64 into each would cost more than the page it decorates. The image is fetched
     * once from its own endpoint and cached by the browser like any other.
     */
    val hasCustomIcon: Boolean = false,
    val accentKey: ProjectAccentKey = ProjectAccentKey.INK,
    val description: String? = null,
    /**
     * Standing instructions for translating this product.
     *
     * The thing a team tells every new translator and then has to tell again — "address the user as
     * du, never Sie", "keep button labels under two words". Distinct from [description], which says
     * what the product is, and from a run's tone, which is a knob somebody turns once. It reaches
     * both the machine-translation prompt and every agent, which is what makes it worth writing.
     */
    val styleGuide: String? = null,
    val locales: List<String> = emptyList(),
    val files: List<FileDto> = emptyList(),
    val progress: List<LocaleProgressDto> = emptyList(),
    val keyCount: Int = 0,
    val fileCount: Int = 0,
    val localeCount: Int = 0,
    /** Null when an older server cannot authoritatively describe editor authority. */
    val editorCapabilities: EditorCapabilitiesDto? = null,
)

@Serializable
data class CreateProjectRequest(
    val name: String,
    val sourceLocale: String,
    /**
     * Which workspace to create the project in.
     *
     * Null means the caller's first workspace, which is right for the overwhelmingly common case of
     * one workspace and wrong the moment someone belongs to two — where "it went somewhere" is not
     * an acceptable answer about a project's billing and access.
     */
    val orgSlug: String? = null,
    val slug: String? = null,
    val targetLocales: List<String> = emptyList(),
    val description: String? = null,
    val kind: ProjectKind = ProjectKind.OTHER,
    val iconKey: ProjectIconKey = ProjectIconKey.MONOGRAM,
    /**
     * Whether this project has an uploaded mark, which supersedes [iconKey] wherever it is drawn.
     *
     * A flag, never the bytes. An icon rides in every project list and every breadcrumb; inlining
     * even 20KB of base64 into each would cost more than the page it decorates. The image is fetched
     * once from its own endpoint and cached by the browser like any other.
     */
    val hasCustomIcon: Boolean = false,
    val accentKey: ProjectAccentKey = ProjectAccentKey.INK,
)

@Serializable
data class UpdateProjectRequest(
    val name: String? = null,
    val description: String? = null,
    val styleGuide: String? = null,
    val sourceLocale: String? = null,
    val kind: ProjectKind? = null,
    val iconKey: ProjectIconKey? = null,
    val accentKey: ProjectAccentKey? = null,
)

@Serializable
data class AddProjectLocaleRequest(
    val locale: String,
)

@Serializable
data class FileDto(
    val id: String,
    val projectId: String,
    val path: String,
    val format: FileFormat,
    val createdAt: String,
    val updatedAt: String,
    /**
     * Live keys in this file. Computed server-side so the project overview can render it directly
     * instead of firing one `listKeys(limit = 1)` request per file to read `.total` — an N+1 that
     * ran inside the page's own load and failed the whole page when any one of them did.
     */
    val keyCount: Int = 0,
)

@Serializable
data class CreateFileRequest(
    val path: String,
    val format: FileFormat,
)

/** Where to send someone after their sample workspace is created. */
@Serializable
data class SampleProjectDto(val slug: String)
